import { useRef, useEffect } from "react";

const MobileImageZoom = ({ src, alt, fallback, setIsZoomed }) => {
  const imgRef = useRef(null);
  const lastTouchRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const posRef = useRef({ x: 0, y: 0 });
  const startDistRef = useRef(0);

  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const applyTransform = () => {
    const img = imgRef.current;
    if (!img) return;

    const scale = scaleRef.current;
    const { x, y } = posRef.current;

    img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;

    if (setIsZoomed) setIsZoomed(scale > 1);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
        lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        };
    }

    if (e.touches.length === 2) {
        startDistRef.current = getDistance(e.touches);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
        const newDist = getDistance(e.touches);

        let newScale = newDist / startDistRef.current;
        newScale = Math.min(Math.max(1, newScale), 3);

        scaleRef.current = newScale;
    }

    if (e.touches.length === 1 && scaleRef.current > 1) {
        const touch = e.touches[0];

        const dx = touch.clientX - lastTouchRef.current.x;
        const dy = touch.clientY - lastTouchRef.current.y;

        posRef.current.x += dx;
        posRef.current.y += dy;

        lastTouchRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        };
    }

    applyTransform();
  };

  const handleTouchEnd = () => {
    lastTouchRef.current = { x: 0, y: 0 };

    if (scaleRef.current === 1) {
        posRef.current = { x: 0, y: 0 };
        applyTransform();
    }
  };

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleWheel = (e) => {
      if (window.innerWidth > 768) return;

      e.preventDefault();
      e.stopPropagation();

      let scale = scaleRef.current;
      const zoomSpeed = 0.15;

      scale += e.deltaY < 0 ? zoomSpeed : -zoomSpeed;
      scale = Math.min(Math.max(1, scale), 3);
      scaleRef.current = scale;

      const rect = img.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const originX = (offsetX / rect.width) * 100;
      const originY = (offsetY / rect.height) * 100;

      img.style.transformOrigin = `${originX}% ${originY}%`;

      applyTransform();
    };

    const parent = img.parentElement;

    parent.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      parent.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <div
      className="overflow-hidden"
      style={{
        touchAction: "none",
        userSelect: "none",
      }}
      onDoubleClick={(e) => e.preventDefault()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="w-full transition-transform duration-100 ease-out"
        onError={(e) => (e.target.src = fallback)}
        style={{ transformOrigin: "center center" }}
      />
    </div>
  );
};

export default MobileImageZoom;